import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import PropTypes from "prop-types";
import ErrorsApi from "../../../../services/API/Errors.Api";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";

const AspenDbLogs = () => {
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  //----------------- Fetching AspenDbLogs -----------------//
  const { data: aspenDbLogs, isFetching } = useQuery(
    ["getAspenDbLog"],
    () => ErrorsApi.getAspenDbLog(),
    {
      initialData: [],
      keepPreviousData: true,
    }
  );

  const handleChangePage = (event: any, newPage: any) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setCurrentPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  //! ---------------------------- TablePaginationActions START--------------------------------//
  function TablePaginationActions(props: any) {
    const theme: any = useTheme();
    const { count, page, rowsPerPage } = props;

    const handleFirstPageButtonClick = (event: any) => {
      setCurrentPage(0);
    };

    const handleBackButtonClick = (event: any) => {
      setCurrentPage(page - 1);
    };

    const handleNextButtonClick = (event: any) => {
      setCurrentPage(page + 1);
    };

    const handleLastPageButtonClick = (event: any) => {
      setCurrentPage(Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
      <div className={classes.root}>
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label="first page"
        >
          {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton
          onClick={handleBackButtonClick}
          disabled={page === 0}
          aria-label="previous page"
        >
          {theme.direction === "rtl" ? (
            <KeyboardArrowRightIcon />
          ) : (
            <KeyboardArrowLeftIcon />
          )}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          {theme.direction === "rtl" ? (
            <KeyboardArrowLeftIcon />
          ) : (
            <KeyboardArrowRightIcon />
          )}
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </div>
    );
  }

  TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
  };
  //! ---------------------------- TablePaginationActions END --------------------------------//

  return (
    <div style={{ margin: "10px" }}>
      <TableContainer component={Paper} sx={{ maxHeight: 560 }}>
        <Table
          className={classes.myTable}
          stickyHeader
          aria-label="sticky table"
        >
          <TableHead>
            <TableRow>
              <TableCell>Database Name</TableCell>
              <TableCell>Exception Detail</TableCell>
              <TableCell>LogId</TableCell>
              <TableCell>Log Message</TableCell>
              <TableCell>Logger</TableCell>
              <TableCell>Server Name</TableCell>
            </TableRow>
          </TableHead>

          {isFetching ? (
            <TableBody>
              <TableCell />
              <TableCell />
              <TableCell />
              <Box my={5}>
                <CircularProgress size={30} />
              </Box>
            </TableBody>
          ) : (
            <>
              <TableBody>
                {aspenDbLogs
                  .slice(
                    currentPage * rowsPerPage,
                    currentPage * rowsPerPage + rowsPerPage
                  )
                  .map((item: any) => (
                    <TableRow hover>
                      <TableCell>{item.databaseName}</TableCell>
                      <TableCell>
                        <Tooltip
                          arrow
                          title={
                            item.exceptionDetail.length > 200
                              ? item.exceptionDetail
                              : ""
                          }
                        >
                          <span>
                            {item.exceptionDetail.length > 200
                              ? item.exceptionDetail.substring(0, 190) + "..."
                              : item.exceptionDetail}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{item.logId}</TableCell>
                      <TableCell>
                        <Tooltip
                          arrow
                          title={
                            item.logMessage.length > 200 ? item.logMessage : ""
                          }
                        >
                          <span>
                            {item.logMessage.length > 200
                              ? item.logMessage.substring(0, 190) + "..."
                              : item.logMessage}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{item.logger}</TableCell>
                      <TableCell>{item.serverName}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </>
          )}
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[15, 20, 50, 100]}
        component="div"
        count={aspenDbLogs.length}
        rowsPerPage={rowsPerPage}
        page={currentPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </div>
  );
};

export default AspenDbLogs;

const useStyles = makeStyles((theme: any) => ({
  root: {
    flexShrink: 0,
  },

  hideCell: {
    display: "none !important",
  },

  myTable: {
    "& th": {
      padding: "5px 5px",
      backgroundColor: "#EAEDED",
    },
    // "& td": {
    //   borderBottom: "1px solid #EAEDED",
    //   height: "0px",
    //   padding: "3px 5px",
    // },
    // "& tr:nth-child(even)": { backgroundColor: "#FBFCFC" },
  },

  myInput: {
    border: "1px solid #BDC3C7",
    padding: "4px 7px",
    width: "100%",
    "&:focus": {
      border: "1px solid #5DADE2",
      outline: "none",
      color: "#5DADE2",
    },
  },
}));
